/**
 * Which provider answers, chosen from settings.
 *
 * Mirrors `repositories/index.ts`: one switch, in one place, and nothing above
 * it knows which branch was taken.
 */
import { loadSettings, modelFor, serverAnswer, type AssistantSettings } from '../settings';
import { createAnthropicProvider } from './anthropic';
import { createLocalProvider } from './local';
import { createOpenAiProvider } from './openai';
import { createProxyProvider } from './proxy';
import { createServerProvider } from './server';
import type { AssistantProvider } from './types';

export function createProvider(settings: AssistantSettings = loadSettings()): AssistantProvider {
  // The app's own server holds the family's key: when it has one, it answers for
  // everybody, whatever an older setting on the family record says.
  const fromServer = serverAnswer(settings);
  if (settings.server && fromServer) {
    return createServerProvider(settings.server, { provider: fromServer.provider, model: settings.model, effort: settings.effort });
  }
  switch (settings.provider) {
    case 'anthropic':
      return createAnthropicProvider({ apiKey: settings.apiKey, model: modelFor(settings), baseUrl: settings.baseUrl });
    case 'openai':
      return createOpenAiProvider({ apiKey: settings.apiKey, model: modelFor(settings), baseUrl: settings.baseUrl });
    case 'proxy':
      return createProxyProvider({ baseUrl: settings.baseUrl, apiKey: settings.apiKey });
    case 'local':
    default:
      return createLocalProvider();
  }
}

export { createLocalProvider };
export type { AskReply, AskRequest, AssistantProvider, ToolCall, ToolDef, ToolResult, Turn } from './types';
export { ProviderError } from './types';
